import images from '@assets/images/index'
import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import {
  GivenChoicesSummary,
  calculateGivenChoicesSummary,
  formatTestsSummary,
  getWDYKReportQuestion,
  printFullTable
} from '@utils/assessmentReportUtils'
import { appSessionStorage } from '@utils/storageUtils'
import { Flex, Spin, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { useNavigate } from '@suntex.core/nav'
import {
  Database_Procs_GetWDYKTestAssessmentByBatch_Item,
  Database_Procs_TeamVIF_Item
} from '@suntex.fim/api-client'
import {
  getAssessmentReportData,
  teamAssessmentInitialSorting,
  useAssessmentOption
} from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { LevelSelect, LoaderOverlay, SearchInput } from '@suntex.fim/ui'

import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import { columns } from './AssessmentReport/AssessmentReportWDYKTable'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'
import WDYKQuestionPreview from './AssessmentReport/WDYKQuestionPreview'

function AssessmentReportWDYK2() {
  const { api } = useFramework()
  useRotateDevice()
  const { wdykLevel: queryWdykLevel } = useAppQueryString()
  const navigate = useNavigate()
  const { jwt, login } = useAuth(isAuthenticated)
  const team = useOptionalTeamInfo()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const selectedLevel = Number(queryWdykLevel) || appSessionStorage.wdykLevel
  const [sendMessageData, setSendMessageData] = useState<any[]>([])

  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }
  const [footerValues, setFooterValues] = useState<(string | null)[]>([])

  const { data: testOptionData, isLoading: isLoadingBatches } = useQuery({
    queryKey: ['assessment', 'wdykBatches', team?.id],
    queryFn: async () => {
      const response = await api.get_Assessment_WdykTestBatchesTeam({
        teamId: team!.id
      })
      const originalWdykTestBatchesByTeams = formatTestsSummary(
        response.wdykTestBatchesByTeams
      )
      const reversedWdykTestBatchesByTeams = Object.entries(
        originalWdykTestBatchesByTeams
      ).reverse()
      return {
        batches: response.wdykTestBatchesByTeams,
        options: reversedWdykTestBatchesByTeams.map(([key, label]) => ({
          key: Number(key),
          label
        }))
      }
    },
    enabled: team?.id != null
  })
  const testOptions = getAssessmentReportData(testOptionData?.options)
  const [level, setLevel] = useAssessmentOption(testOptions, selectedLevel)
  const testTemplateId = testOptionData?.batches.find(
    (batch) => batch.id === level
  )?.testTemplateId
  const memoColumns = useMemo<
    ColumnType<Database_Procs_GetWDYKTestAssessmentByBatch_Item>[]
  >(
    () =>
      columns({
        sendMessage,
        showQuestion: async (columnId) => {
          try {
            const question = await getWDYKReportQuestion(
              api,
              testTemplateId ?? 0,
              columnId
            )
            message.info(<WDYKQuestionPreview question={question} />)
          } catch {
            message.error('Unable to load this question. Please try again.')
          }
        }
      }),
    [level, api, testTemplateId]
  )

  const { data: assessmentReportData, isLoading: isLoadingRecords } = useQuery({
    queryKey: ['assessment', 'wdykRecords', level],
    queryFn: async () => {
      const response = await api.get_Assessment_WdykTestAssessmentByBatch({
        batchId: level
      })
      return response.wdykTestAssessmentByBatches || []
    },
    enabled: level != null && level > 0
  })
  const assessmentReport = getAssessmentReportData(assessmentReportData)

  const isLoading = isLoadingBatches || isLoadingRecords || isExporting
  const givenChoices = calculateGivenChoicesSummary(assessmentReport)

  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const { table, Table } =
    useAssessmentTable<Database_Procs_GetWDYKTestAssessmentByBatch_Item>({
      data: assessmentReport,
      columns: memoColumns,
      isLoading,
      globalFilter: searchQuery,
      horizontalScroll: true,
      initialSorting: teamAssessmentInitialSorting,
      displayFooter: true,
      footerValues
    })

  // Recalculate footer values whenever the table changes or headers are updated
  useEffect(() => {
    if (table.getHeaderGroups().length > 0) {
      const { headers } = table.getHeaderGroups()[0]
      // Convert _1 to _10 into givenChoice1 to givenChoice10
      const choiceValues: Record<`_${number}`, number> = Object.fromEntries(
        Array.from({ length: 10 }, (_, i) => {
          const key = `_${i + 1}`
          return [
            `_${i + 1}`,
            Number(givenChoices[key as keyof GivenChoicesSummary] || 0)
          ]
        })
      )
      // Map of visible columns (match column IDs like _1, _2 to _1, _2)
      const visibleColumnsMap = Object.fromEntries(
        headers
          .filter((header) => /^_[1-9]$|^_[1-9][0-9]$/.test(header.id)) // Filter out non-numbered columns
          .map((header) => [`_${header.id.slice(1)}`, true]) // Convert _1 to _1, etc.
      ) as Record<`_${number}`, boolean>

      // Total count from givenChoices
      const totalCount = givenChoices.length

      // Construct footer values ensuring only visible values are included
      const nextFooterValues = [
        null,
        null,
        null,
        null,
        null,
        ...Array.from({ length: 10 }, (_, i) => {
          const key = `_${i + 1}`
          if (!visibleColumnsMap[key]) return null // Skip hidden columns
          return `${choiceValues[key]}/${totalCount}`
        })
      ]
      setFooterValues(nextFooterValues)
    }
  }, [table, assessmentReport])

  const selectedRow = table.getSelectedRowModel().flatRows
  const isDisabled = !selectedRow.length
  // Update localStorage whenever level changes
  useEffect(() => {
    appSessionStorage.wdykLevel = level
  }, [level])

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: Database_Procs_TeamVIF_Item }) => ({
      label: original.displayName || original.userId,
      value: original.playerId
    })
  )
  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay isLoading={isLoading} />
        <AssessmentReportNavigation
          exportButton={
            <Flex gap={10} className="export-btn print-hidden">
              <Button
                shape="round"
                intent="primary"
                onClick={() =>
                  navigate(
                    `/team/${team?.id}/assessment-report/what-do-you-know-test`
                  )
                }
              >
                Create test <images.AddCircleWhite />
              </Button>
              <Button
                shape="round"
                intent="secondary"
                onClick={() => {
                  void printFullTable(table, {
                    setIsLoading: setIsExporting,
                    fileName: `${team?.name}_WDYK`
                  })
                }}
              >
                View Printer Friendly Version
              </Button>
            </Flex>
          }
          filters={
            <Flex gap={14}>
              {testOptions.length > 0 ? (
                <Flex gap={6} flex="1 1 auto" vertical>
                  <p className="dd-label fw-bold text-sm">Test</p>
                  {isLoading ? (
                    <Spin />
                  ) : (
                    <LevelSelect
                      value={level}
                      onChange={(selectedLevel) => {
                        setLevel(selectedLevel)
                        setSearchQuery('')
                      }}
                      options={testOptions}
                    />
                  )}
                </Flex>
              ) : (
                'There are no What Do You Know 2.0 tests administered for this team'
              )}
            </Flex>
          }
          // title="What Do You Know"
        />
        {/* result */}
        {testOptions.length > 0 && (
          <div className="main-result">
            {/* Updated toolbar design */}
            <Flex
              justify="space-between"
              align="center"
              className="main-result__toolbar"
              wrap
            >
              <h4 className="title">Results</h4>

              <Flex
                gap="10px"
                justify="space-between"
                wrap
                className="print-hidden"
              >
                <div className="report-search">
                  <SearchInput value={searchQuery} onChange={setSearchQuery} />
                </div>
                <Button
                  intent="tertiary"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => setIsModalOpen(true)}
                >
                  SEND MESSAGE
                </Button>
              </Flex>
            </Flex>
            <Table />
          </div>
        )}
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)

            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
        />
      </Flex>
    </>
  )
}

export default AssessmentReportWDYK2
